import { useState, useCallback, useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { RowClassParams } from 'ag-grid-community'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { ApiTokenManagementRow } from './ApiTokenManagementRow'
import { useGridOptions } from './gridOptions/useGridOptions'
import { createGetContextMenuItems } from './gridOptions/contextMenu'
import { useApiTokenManagementData } from './hooks/useApiTokenManagementData'
import ApiTokenManagementHeader from './components/Header'
import CreateTokenDialog from './components/CreateTokenDialog'
import TokenDisplayModal from './components/TokenDisplayModal'
import RevokeConfirmDialog from './components/RevokeConfirmDialog'
import Loading from '../../components/process-state-notifications/Loading'
import { colorPalette } from '../../style/colorPalette'
import { PageArea } from '..'

type Props = {
  uuid: string
}

const ApiTokenManagement = ({ uuid: functionUuid }: Props) => {
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<AgGridReact<ApiTokenManagementRow>>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [displayedToken, setDisplayedToken] = useState<string | null>(null)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] =
    useState<ApiTokenManagementRow | null>(null)

  const {
    rowData,
    availableScopes,
    loading,
    onReload,
    onCreateServiceToken,
    onRevokeToken,
  } = useApiTokenManagementData(functionUuid)

  const gridOptions = useGridOptions()

  const onRevokeClick = useCallback((row: ApiTokenManagementRow) => {
    setRevokeTarget(row)
    setRevokeDialogOpen(true)
  }, [])

  const onConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return
    const success = await onRevokeToken(
      revokeTarget.uuid,
      revokeTarget.tokenType
    )
    if (success) {
      setRevokeDialogOpen(false)
      setRevokeTarget(null)
    }
  }, [revokeTarget, onRevokeToken])

  const onCancelRevoke = useCallback(() => {
    setRevokeDialogOpen(false)
    setRevokeTarget(null)
  }, [])

  const getContextMenuItems = useMemo(
    () => createGetContextMenuItems(onRevokeClick),
    [onRevokeClick]
  )

  const onOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const onCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  const onSubmitCreate = useCallback(
    async ({
      serviceName,
      expiredAt,
      scopes,
    }: {
      serviceName: string
      expiredAt: number
      scopes: string[]
    }) => {
      const rawToken = await onCreateServiceToken({
        serviceName,
        expiredAt,
        scopes,
      })
      if (rawToken) {
        setCreateDialogOpen(false)
        setDisplayedToken(rawToken)
        setTokenModalOpen(true)
      }
    },
    [onCreateServiceToken]
  )

  const onCloseTokenModal = useCallback(() => {
    setTokenModalOpen(false)
    setDisplayedToken(null)
  }, [])

  const getRowStyle = useCallback(
    ({ data }: RowClassParams<ApiTokenManagementRow>) => {
      if (data?.revoked || data?.expired) {
        return { backgroundColor: colorPalette.monotone[2] }
      }
      return undefined
    },
    []
  )

  return (
    <PageArea>
      <ApiTokenManagementHeader
        loading={loading}
        functionUuid={functionUuid}
        onReload={onReload}
        onClickCreateToken={onOpenCreateDialog}
      />
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rowData}
        getContextMenuItems={getContextMenuItems}
        getRowStyle={getRowStyle}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
      <CreateTokenDialog
        open={createDialogOpen}
        onClose={onCloseCreateDialog}
        onSubmit={onSubmitCreate}
        loading={loading}
        availableScopes={availableScopes}
      />
      <TokenDisplayModal
        open={tokenModalOpen}
        rawToken={displayedToken}
        onClose={onCloseTokenModal}
      />
      <RevokeConfirmDialog
        open={revokeDialogOpen}
        tokenName={revokeTarget?.name ?? ''}
        onConfirm={onConfirmRevoke}
        onCancel={onCancelRevoke}
        loading={loading}
      />
    </PageArea>
  )
}

export default ApiTokenManagement
